import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';

import { fetchOverview, overviewSelectors } from '@crm/features/overview/overviewSlice';
import { useBranchRealtime } from '@crm/hooks/useBranchRealtime';

import './DashboardPage.scss';

const DashboardOverviewGrid = lazy(() => import('./DashboardOverviewGrid.jsx'));

export function DashboardPage() {
  const { ownerId, branchId } = useParams();
  const dispatch = useDispatch();
  const data = useSelector(overviewSelectors.select(branchId));

  const [monthFrom, setMonthFrom] = useState(() => format(new Date(), 'yyyy-MM'));
  const [monthTo, setMonthTo] = useState(() => format(new Date(), 'yyyy-MM'));
  const [revenueDay, setRevenueDay] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [emplMonth, setEmplMonth] = useState(() => format(new Date(), 'yyyy-MM'));

  const refetch = useCallback(() => {
    if (!branchId) return;
    dispatch(
      fetchOverview({
        branchId,
        monthFrom,
        monthTo,
        revenueDay,
        employmentMonth: emplMonth,
      }),
    );
  }, [dispatch, branchId, monthFrom, monthTo, revenueDay, emplMonth]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useBranchRealtime(branchId, (action) => {
    if (
      action.startsWith('sales:') ||
      action.startsWith('calendar:') ||
      action.startsWith('clients:')
    ) {
      refetch();
    }
  });

  // Виджет «Пробные сегодня» должен отражать текущее состояние CRM, поэтому
  // обновляемся при возврате на вкладку (ТЗ «Дата пробного», §34).
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [refetch]);

  if (!data || data.status === 'loading') {
    return <div className="placeholder">Загрузка…</div>;
  }

  if (data.status === 'error') {
    return (
      <div className="placeholder" style={{ color: 'var(--color-danger)' }}>
        {data.error ?? 'Ошибка загрузки'}
      </div>
    );
  }

  const counts = data.counts ?? null;

  return (
    <section className="crm-dashboard">
      <header className="page-header">
        <h1>Обзор филиала</h1>
      </header>

      {counts && (
        <div className="crm-dashboard__counts">
          <div className="crm-dashboard__count-card">
            <div className="label">Клиенты</div>
            <div className="value">{counts.clients ?? 0}</div>
          </div>
          <div className="crm-dashboard__count-card">
            <div className="label">Направления</div>
            <div className="value">{counts.directions ?? 0}</div>
          </div>
          <div className="crm-dashboard__count-card">
            <div className="label">Сотрудники</div>
            <div className="value">{counts.managers ?? 0}</div>
          </div>
        </div>
      )}

      <Suspense fallback={<div className="placeholder">Загрузка графиков…</div>}>
        <DashboardOverviewGrid
          data={data}
          ownerId={ownerId}
          branchId={branchId}
          monthFrom={monthFrom}
          setMonthFrom={setMonthFrom}
          monthTo={monthTo}
          setMonthTo={setMonthTo}
          revenueDay={revenueDay}
          setRevenueDay={setRevenueDay}
          emplMonth={emplMonth}
          setEmplMonth={setEmplMonth}
          dispatch={dispatch}
        />
      </Suspense>
    </section>
  );
}
